import { z } from "zod";

/**
 * The telemetry wire shape, shared by the client (telemetry.ts) and the
 * function that receives it (api/telemetry.ts). Two kinds of upload
 * travel the same endpoint: a batch of events, and a feedback note with
 * the player's save file attached. Everything here is anonymous by
 * construction — the ids are random strings the client minted, and no
 * field carries a name, an address, or a cookie.
 *
 * Every event carries two clocks and a position. `ts` is the wall
 * clock; `play` is the seconds of play so far this session — time the
 * game was running, not paused and not hidden in a background tab — so
 * a step's duration can be read with the pause menu and the lunch
 * break taken out. `at` is where on the demo's spine the player was
 * (spine.ts: the id of the furthest milestone reached), so anything
 * that happened can be grouped by the step it happened during.
 *
 * Nothing else imports zod on the server side, so keep this file free
 * of browser and engine imports: the function bundles it on its own.
 */

/** Events per batch; the client drops the rest rather than grow without bound. */
export const MAX_EVENTS_PER_BATCH = 500;
/** A feedback note's length, in characters. */
export const MAX_NOTE_LENGTH = 4000;

const id = z.string().min(1).max(64);

export const telemetryEventSchema = z.object({
  name: z.string().min(1).max(64),
  /** Wall-clock milliseconds since the epoch, as the client saw them. */
  ts: z.number().int().nonnegative(),
  /** Seconds of play so far this session: unpaused, tab visible. */
  play: z.number().nonnegative(),
  /**
   * The spine position: the furthest milestone reached, or "start".
   * Absent only before the world has loaded (the session's first
   * events), when the client can't know where the save stands.
   */
  at: z.string().min(1).max(64).optional(),
  props: z.record(z.unknown()).optional(),
});

const envelope = {
  v: z.literal(1),
  sessionId: id,
  playerId: id,
  build: z.string().max(64),
};

export const eventsUploadSchema = z.object({
  kind: z.literal("events"),
  ...envelope,
  events: z.array(telemetryEventSchema).min(1).max(MAX_EVENTS_PER_BATCH),
});

/** A save file's outline — enough to know it is one, not its contents. */
const saveOutlineSchema = z.object({
  version: z.number().int(),
  singletons: z.record(z.unknown()),
  entities: z.array(z.unknown()),
});

export const feedbackUploadSchema = z.object({
  kind: z.literal("feedback"),
  ...envelope,
  /** Where on the spine the note was written from, like an event's. */
  at: z.string().min(1).max(64).optional(),
  note: z.string().trim().min(1).max(MAX_NOTE_LENGTH),
  save: saveOutlineSchema.nullable(),
});

export const uploadSchema = z.discriminatedUnion("kind", [
  eventsUploadSchema,
  feedbackUploadSchema,
]);

export type TelemetryEvent = z.infer<typeof telemetryEventSchema>;
export type EventsUpload = z.infer<typeof eventsUploadSchema>;
export type FeedbackUpload = z.infer<typeof feedbackUploadSchema>;
export type Upload = z.infer<typeof uploadSchema>;

/** The parsed upload, or null for anything that isn't one. */
export function parseUpload(body: unknown): Upload | null {
  const result = uploadSchema.safeParse(body);
  return result.success ? result.data : null;
}
